import { fetchSiteLogs } from '@/remote/site-management/fetch-site-logs';
import type { SiteLogQuery, SiteLogResponse } from '@/types/site';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

interface UseSiteLogOptions extends SiteLogQuery {
  siteId: string;
  enabled?: boolean;
}

interface UseSiteLogReturn {
  logs: SiteLogResponse['logs'];
  pagination: SiteLogResponse['pagination'];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  lowWaterLevel: number;
  highWaterLevel: number;
}

export const useSiteLog = ({
  siteId,
  page = 1,
  limit = 10,
  startDate,
  endDate,
  enabled = true,
}: UseSiteLogOptions): UseSiteLogReturn => {
  const queryResult: UseQueryResult<SiteLogResponse> = useQuery({
    queryKey: ['siteLogs', siteId, page, limit, startDate, endDate],
    queryFn: async () => {
      const response = await fetchSiteLogs(siteId, {
        page,
        limit,
        startDate,
        endDate,
      });
      return response.data;
    },
    enabled: enabled && !!siteId,
    staleTime: 30000, // 30초
    refetchInterval: 60000, // 1분마다 자동 갱신
  });

  return {
    logs: queryResult.data?.logs || [],
    pagination: queryResult.data?.pagination || {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: limit,
    },
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    lowWaterLevel: queryResult.data?.lowWaterLevel ?? 40,
    highWaterLevel: queryResult.data?.highWaterLevel ?? 100,
    refetch: () => {
      queryResult.refetch();
    },
  };
};
